import { useState, useEffect, type ComponentType } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Paperclip,
  Trash2,
  Download,
  MessageSquare,
  Activity,
  FileText,
  Save,
  Edit3,
  Lock,
  UserPlus,
  Settings2,
  Image,
  X,
  type LucideProps,
} from "lucide-react";
import {
  api,
  apiError,
  type Ticket as TicketType,
  type Comment as CommentType,
  type Attachment,
  type ActivityLog,
  type UserSummary,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard } from "../components/ui/GlassCard";
import { PriorityBadge } from "../components/tickets/PriorityBadge";
import { StatusBadge } from "../components/tickets/StatusBadge";
import { CategoryBadge } from "../components/tickets/CategoryBadge";
import { SLACountdown } from "../components/tickets/SLACountdown";
import { Avatar } from "../components/ui/Avatar";
import { Spinner } from "../components/ui/FullScreenLoader";
import { EmptyState } from "../components/ui/EmptyState";
import { fmtDateTime, fmtRelative } from "../lib/format";
import {
  PRIORITIES,
  STATUSES,
  CATEGORIES,
  STATUS_META,
  PRIORITY_META,
  CATEGORY_META,
} from "../lib/constants";
import { cn } from "../lib/cn";
import { exportTicketPdf } from "../lib/exportTicketPdf";

const TABS: { id: TabId; label: string; icon: ComponentType<LucideProps> }[] = [
  { id: "details", label: "Details", icon: FileText },
  { id: "comments", label: "Comments", icon: MessageSquare },
  { id: "attachments", label: "Attachments", icon: Paperclip },
  { id: "activity", label: "Activity", icon: Activity },
];

type TabId = "details" | "comments" | "attachments" | "activity";

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isStaff, isAgent } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabId>("details");

  const [isCustomIssue, setIsCustomIssue] = useState(false);
  const [customIssueText, setCustomIssueText] = useState("");

  const ticketQ = useQuery({
    queryKey: ["ticket", id],
    queryFn: async () =>
      (await api.get<{ ticket: TicketType }>(`/api/tickets/${id}`)).data.ticket,
  });

  const commentsQ = useQuery({
    queryKey: ["ticket.comments", id],
    queryFn: async () =>
      (await api.get<{ items: CommentType[] }>(`/api/tickets/${id}/comments`)).data
        .items,
    enabled: !!ticketQ.data,
    // Live conversation: poll while viewing the Comments tab so replies from
    // other users appear without a manual refresh.
    refetchInterval: tab === "comments" ? 5_000 : false,
    refetchOnWindowFocus: true,
  });

  const activityQ = useQuery({
    queryKey: ["ticket.activity", id],
    queryFn: async () =>
      (await api.get<{ items: ActivityLog[] }>(`/api/tickets/${id}/activity`)).data
        .items,
    enabled: !!ticketQ.data,
  });

  const assigneesQ = useQuery({
    queryKey: ["users.assignees"],
    queryFn: async () => {
      const items = (await api.get<{ items: UserSummary[] }>("/api/users/assignees")).data.items;
      return items.filter((u) => u.role === "IT" || u.role === "ADMIN");
    },
    enabled: isStaff,
  });

  const patch = useMutation({
    mutationFn: async (changes: Partial<TicketType> & Record<string, unknown>) =>
      (await api.patch(`/api/tickets/${id}`, changes)).data.ticket,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", id] });
      qc.invalidateQueries({ queryKey: ["ticket.activity", id] });
      qc.invalidateQueries({ queryKey: ["tickets.list"] });
      qc.invalidateQueries({ queryKey: ["dashboard.recent"] });
      toast.success("Ticket updated");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const delMut = useMutation({
    mutationFn: async () => (await api.delete(`/api/tickets/${id}`)).data,
    onSuccess: () => {
      toast.success("Ticket deleted");
      qc.invalidateQueries({ queryKey: ["tickets.list"] });
      navigate("/tickets");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (ticketQ.isLoading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-10 w-32" />
        <div className="skeleton h-32" />
        <div className="skeleton h-64" />
      </div>
    );
  }

  if (ticketQ.isError || !ticketQ.data) {
    return (
      <GlassCard>
        <EmptyState
          title="Ticket not found"
          description="It may have been deleted or you may not have access."
          action={
            <Link to="/tickets" className="btn-primary inline-flex">
              Back to tickets
            </Link>
          }
        />
      </GlassCard>
    );
  }

  const t = ticketQ.data;
  const isOwner = t.submitterId === user?.id;
  const canEditMeta = isStaff;
  const canEditContent = isStaff || (isOwner && t.status !== "CLOSED");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 print:hidden">
        <button onClick={() => navigate(-1)} className="btn-ghost">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          {t.status !== "CLOSED" && (
            <button
              onClick={() => {
                if (confirm("Are you sure you want to close this ticket? You will not be able to edit it afterwards.")) {
                  patch.mutate({ status: "CLOSED" });
                }
              }}
              className="btn-ghost text-critical-300 hover:text-critical-100 hover:bg-critical-500/20 border border-critical-500/30"
              disabled={patch.isPending}
            >
              <Lock className="w-4 h-4" /> Close Ticket
            </button>
          )}
          <button
            onClick={() => exportTicketPdf(t)}
            className="btn-ghost text-slate-300 hover:text-white"
          >
            <Download className="w-4 h-4" /> Export PDF
          </button>
          {(isStaff || isOwner) && (
            <button
              onClick={() => {
                if (confirm("Delete this ticket? This cannot be undone."))
                  delMut.mutate();
              }}
              className="btn-ghost text-critical-200 border-critical-400/30 hover:bg-critical-500/10"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Header card */}
      <GlassCard strong>
        <div className="flex flex-col lg:flex-row lg:items-start gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <PriorityBadge priority={t.priority} />
              <StatusBadge status={t.status} />
              <CategoryBadge category={t.category} />
              <span className="text-xs text-slate-400">
                #{t.id.slice(-8).toUpperCase()}
              </span>
              {t.issue && (
                <>
                  <span className="text-slate-500 text-xs">·</span>
                  <span className="text-xs font-medium text-slate-300">
                    {t.issue}
                  </span>
                </>
              )}
            </div>
            <h1 className="text-2xl font-semibold text-white text-balance">
              {t.title}
            </h1>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-400 flex-wrap">
              <Avatar name={t.submitter?.name || "?"} url={t.submitter?.avatarUrl} size="xs" />
              <span>
                Submitted by{" "}
                <span className="text-slate-200">{t.submitter?.name}</span>
                {t.submitter?.department && (
                  <span className="text-slate-500"> · {t.submitter.department}</span>
                )}
              </span>
              <span>·</span>
              <span>{fmtRelative(t.createdAt)}</span>
              {t.resolvedAt && (
                <>
                  <span>·</span>
                  <span className="text-emerald-300">
                    Resolved {fmtRelative(t.resolvedAt)}
                  </span>
                </>
              )}
            </div>
          </div>
          <SLACountdown
            deadline={t.slaDeadline}
            priority={t.priority}
            status={t.status}
          />
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="glass-subtle rounded-2xl p-1 inline-flex flex-wrap">
            {TABS.map(({ id: tabId, label, icon: Icon }) => {
              const active = tab === tabId;
              const count =
                tabId === "comments"
                  ? commentsQ.data?.length
                  : tabId === "attachments"
                    ? t.attachments?.length
                    : null;
              return (
                <button
                  key={tabId}
                  onClick={() => setTab(tabId)}
                  className={cn(
                    "px-3 py-1.5 rounded-xl text-sm flex items-center gap-2 transition-all",
                    active
                      ? "bg-white/10 text-white shadow-glass-sm"
                      : "text-slate-300 hover:bg-white/5",
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                  {count !== null && count !== undefined && (
                    <span className="text-[10px] text-slate-400 bg-white/5 rounded-full px-1.5 py-0.5">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {tab === "details" && (
            <DetailsTab
              ticket={t}
              canEdit={canEditContent}
              onSave={(d) => patch.mutate(d)}
              saving={patch.isPending}
              activityLogs={activityQ.data || []}
              assignees={assigneesQ.data || []}
            />
          )}
          {tab === "comments" && (
            <CommentsTab
              ticketId={id!}
              comments={commentsQ.data || []}
              loading={commentsQ.isLoading}
              isStaff={isStaff}
              currentUserId={user?.id || ""}
            />
          )}
          {tab === "attachments" && (
            <AttachmentsTab
              ticketId={id!}
              attachments={t.attachments || []}
              canDelete={(att) => att.uploaderId === user?.id || isStaff}
            />
          )}
          {tab === "activity" && (
            <ActivityTab items={activityQ.data || []} loading={activityQ.isLoading} />
          )}
        </div>

        {/* Side: controls */}
        <div className="space-y-4">
          {canEditMeta && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Settings2 className="w-4 h-4" /> Controls
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="label">Status</label>
                  <select
                    className="select"
                    value={t.status}
                    onChange={(e) => patch.mutate({ status: e.target.value as any })}
                    disabled={patch.isPending}
                  >
                    {STATUSES.map((s) => {
                      const isResolveOrClose = s === 'RESOLVED' || s === 'CLOSED';
                      const isAssignedToMe = t.assigneeId === user.id;
                      const isAdmin = user.role === 'ADMIN';
                      // If the user is staff but not admin, they can only resolve if assigned to them.
                      // Submitters cannot change status using this dropdown (it is hidden for them anyway).
                      const disabledOption = isResolveOrClose && isStaff && !isAdmin && !isAssignedToMe && t.status !== s;
                      return (
                        <option key={s} value={s} disabled={disabledOption}>
                          {STATUS_META[s].label}
                        </option>
                      );
                    })}
                  </select>
                  {isStaff && user.role !== 'ADMIN' && t.assigneeId !== user.id && t.status !== 'RESOLVED' && t.status !== 'CLOSED' && (
                    <p className="text-[10px] text-amber-400/80 mt-1">
                      You must be assigned to resolve.
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">Priority</label>
                  <select
                    className="select"
                    value={t.priority}
                    onChange={(e) =>
                      patch.mutate({ priority: e.target.value as any })
                    }
                    disabled={patch.isPending}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_META[p].label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Category</label>
                  <select
                    className="select"
                    value={t.category}
                    onChange={(e) =>
                      patch.mutate({ category: e.target.value as any })
                    }
                    disabled={patch.isPending}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {CATEGORY_META[c].label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Assignee</label>
                  <select
                    className="select"
                    value={t.assigneeId || ""}
                    onChange={(e) =>
                      patch.mutate({ assigneeId: e.target.value || null })
                    }
                    disabled={patch.isPending}
                  >
                    <option value="">— Unassigned —</option>
                    {(assigneesQ.data || []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.role})
                      </option>
                    ))}
                  </select>
                </div>
                {!t.assigneeId && (
                  <button
                    onClick={() => patch.mutate({ assigneeId: user?.id })}
                    className="btn-ghost w-full"
                    disabled={patch.isPending}
                  >
                    <UserPlus className="w-4 h-4" /> Assign to me
                  </button>
                )}
              </div>
            </GlassCard>
          )}

          {isAgent && !canEditMeta && (
            <GlassCard>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Settings2 className="w-4 h-4" /> Controls
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="label">Status</label>
                  <select
                    className="select opacity-70 cursor-not-allowed"
                    value={t.status}
                    disabled
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_META[s].label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Priority</label>
                  <select
                    className="select opacity-70 cursor-not-allowed"
                    value={t.priority}
                    disabled
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_META[p].label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Category</label>
                  <select
                    className="select opacity-70 cursor-not-allowed"
                    value={t.category}
                    disabled
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {CATEGORY_META[c].label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Assignee</label>
                  <select
                    className="select opacity-70 cursor-not-allowed"
                    value={t.assigneeId || ""}
                    disabled
                  >
                    <option value="">— Unassigned —</option>
                    {(assigneesQ.data || []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.role})
                      </option>
                    ))}
                  </select>
                </div>
                {t.status !== "RESOLVED" && t.status !== "CLOSED" && (
                  <button
                    onClick={() => patch.mutate({ status: "RESOLVED" })}
                    className="btn-primary w-full mt-4"
                    disabled={patch.isPending}
                  >
                    Mark as Resolved
                  </button>
                )}
              </div>
            </GlassCard>
          )}

          <GlassCard>
            <h3 className="text-sm font-semibold text-white mb-3">Info</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-400">Created</dt>
                <dd className="text-slate-200">{fmtDateTime(t.createdAt)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-400">Updated</dt>
                <dd className="text-slate-200">{fmtDateTime(t.updatedAt)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-400">SLA deadline</dt>
                <dd className="text-slate-200">{fmtDateTime(t.slaDeadline)}</dd>
              </div>
              {t.resolvedAt && (
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Resolved at</dt>
                  <dd className="text-slate-200">{fmtDateTime(t.resolvedAt)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-2 border-t border-white/5 pt-2 mt-2">
                <dt className="text-slate-400">Submitter</dt>
                <dd className="text-slate-200 text-right flex flex-col items-end gap-0.5">
                  {t.submitter ? (
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <Avatar name={t.submitter.name} url={t.submitter.avatarUrl} size="xs" />
                        {t.submitter.name}
                      </span>
                      {t.submitter.email && <span className="text-[11px] text-slate-400">{t.submitter.email}</span>}
                      {t.submitter.phone_number && <span className="text-[11px] text-slate-400">{t.submitter.phone_number}</span>}
                    </>
                  ) : (
                    <span className="italic text-slate-500">Unknown</span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between gap-2 border-t border-white/5 pt-2 mt-2">
                <dt className="text-slate-400">Assignee</dt>
                <dd className="text-slate-200 text-right flex flex-col items-end gap-0.5">
                  {t.assignee ? (
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <Avatar name={t.assignee.name} url={t.assignee.avatarUrl} size="xs" />
                        {t.assignee.name}
                      </span>
                      {t.assignee.email && <span className="text-[11px] text-slate-400">{t.assignee.email}</span>}
                      {t.assignee.phone_number && <span className="text-[11px] text-slate-400">{t.assignee.phone_number}</span>}
                    </>
                  ) : (
                    <span className="italic text-slate-500">Unassigned</span>
                  )}
                </dd>
              </div>
              <div className="border-t border-white/5 my-2" />
              <div className="flex justify-between gap-2">
                <dt className="text-slate-400">Email</dt>
                <dd className="text-slate-200 text-xs truncate max-w-[180px]" title={t.submitter.email}>
                  {t.submitter.email || <span className="italic text-slate-500">—</span>}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-400">Phone</dt>
                <dd className="text-slate-200">
                  {t.submitter.phone_number || <span className="italic text-slate-500">—</span>}
                </dd>
              </div>
            </dl>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}

// ---------- AttachmentImage ----------------------------------------------
function AttachmentImage({
  attachmentId,
  alt,
  className,
}: {
  attachmentId: string;
  alt: string;
  className?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let url: string | null = null;

    const fetchImage = async () => {
      try {
        const res = await api.get(`/api/attachments/${attachmentId}`, {
          responseType: "blob",
        });
        if (!active) return;
        url = URL.createObjectURL(new Blob([res.data]));
        setSrc(url);
      } catch (err) {
        console.error("Failed to load image attachment", err);
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchImage();

    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachmentId]);

  if (loading) {
    return <div className="skeleton w-full h-full min-h-[120px] rounded-lg" />;
  }

  if (error || !src) {
    return (
      <div className="w-full h-full min-h-[120px] rounded-lg bg-white/[0.02] border border-white/10 flex flex-col items-center justify-center text-[10px] text-slate-400 gap-1 p-2">
        <Paperclip className="w-4 h-4 text-slate-500" />
        <span className="truncate max-w-full">{alt}</span>
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} />;
}

// ---------- AssignmentTimeline ----------------------------------------------
function AssignmentTimeline({ items, assignees }: { items: ActivityLog[], assignees?: UserSummary[] }) {
  const assignmentLogs = items.filter((a) => {
    const act = a.action.toLowerCase();
    return (
      (a.meta && (a.meta as any).type === "assignment") ||
      act.includes("assigned") ||
      act.includes("reassigned") ||
      act.includes("unassigned") ||
      act.includes("assignee")
    );
  });

  if (assignmentLogs.length === 0) {
    return (
      <div className="text-center py-8 bg-white/[0.01] border border-white/5 rounded-2xl">
        <p className="text-xs text-slate-500 italic">
          No assignment history recorded yet.
        </p>
      </div>
    );
  }

  return (
    <div className="relative border-l border-white/10 ml-4 pl-6 space-y-6 py-2">
      {assignmentLogs.map((log) => {
        const isAuto = log.action.toLowerCase().includes("auto");

        // Extract assignee name for Avatar. If legacy action, parse name from action string.
        let assigneeName = "";
        const meta = log.meta as any;
        if (meta && meta.toName) {
          assigneeName = meta.toName;
        } else {
          const match = log.action.match(
            /(?:Assigned to|Reassigned from .* to|Reassigned to) (.*)/i,
          );
          assigneeName = match?.[1]
            ? match[1].replace(/\(auto-assigned\)/i, "").trim()
            : "";
        }

        // Clean action string to not show raw database ids
        let cleanAction = log.action;
        if (!meta?.type && /^[c|u][a-z0-9]{24}$/i.test(assigneeName)) {
          cleanAction =
            log.action.toLowerCase().includes("remove") ||
              log.action.toLowerCase().includes("unassign")
              ? "Assignee removed"
              : "Ticket assigned";
          assigneeName = "User";
        }

        let assigneeAvatar: string | null | undefined = null;
        if (assignees && assigneeName) {
          const matchUser = assignees.find(a => a.name === assigneeName);
          if (matchUser) assigneeAvatar = matchUser.avatarUrl;
        }

        return (
          <div
            key={log.id}
            className="relative group transition-all duration-300 hover:translate-x-1"
          >
            {/* Timeline bullet dot with glowing pulse effect */}
            <span className="absolute -left-[30px] top-1.5 w-3 h-3 rounded-full bg-brand-400 border border-brand-300 shadow-[0_0_8px_rgba(59,130,246,0.5)] ring-4 ring-[#080b24] group-hover:scale-125 group-hover:bg-brand-300 transition-all duration-300" />

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <Avatar name={assigneeName || "U"} url={assigneeAvatar} size="xs" />
                <span className="text-sm font-semibold text-slate-200">
                  {cleanAction}
                </span>
                {isAuto && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-300 border border-teal-500/20">
                    Auto
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-400 whitespace-nowrap bg-white/[0.02] border border-white/5 rounded-lg px-2.5 py-1 self-start sm:self-auto font-mono">
                {fmtDateTime(log.createdAt)}
              </div>
            </div>

            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400">
              <span>Updated by</span>
              <span className="text-slate-300 font-semibold">
                {log.user?.name || "System"}
              </span>
              <span className="text-slate-500">({log.user?.role || "SYSTEM"})</span>
              <span>·</span>
              <span className="text-slate-500 text-[10px]">
                {fmtRelative(log.createdAt)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- DetailsTab ---------------------------------------------------
function DetailsTab({
  ticket,
  canEdit,
  onSave,
  saving,
  activityLogs = [],
  assignees = [],
}: {
  ticket: TicketType;
  canEdit: boolean;
  onSave: (d: { title: string; description: string }) => void;
  saving: boolean;
  activityLogs?: ActivityLog[];
  assignees?: UserSummary[];
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);
  const [lightboxImage, setLightboxImage] = useState<Attachment | null>(null);

  const images =
    ticket.attachments?.filter((att) => att.mimeType.startsWith("image/")) || [];

  if (editing) {
    return (
      <GlassCard>
        <input
          className="input mb-3"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="textarea"
          rows={10}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            className="btn-ghost"
            onClick={() => {
              setTitle(ticket.title);
              setDescription(ticket.description);
              setEditing(false);
            }}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={saving}
            onClick={() => {
              onSave({ title, description });
              setEditing(false);
            }}
          >
            {saving ? <Spinner /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </GlassCard>
    );
  }

  return (
    <>
      <GlassCard>
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold text-white">Description</h3>
          {canEdit ? (
            <button onClick={() => setEditing(true)} className="btn-ghost">
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
              <Lock className="w-3 h-3" /> Read-only
            </span>
          )}
        </div>
        <div className="mt-2 max-h-[400px] overflow-y-auto pr-2">
          <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
            {ticket.description}
          </p>
        </div>

        {images.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/5 space-y-3">
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Image className="w-3.5 h-3.5 text-brand-300" />
              Attached Screenshots ({images.length})
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[480px] overflow-y-auto pr-2">
              {images.map((img) => (
                <div
                  key={img.id}
                  className="group relative rounded-xl border border-white/10 bg-slate-950/40 p-2 overflow-hidden hover:border-white/20 transition-all duration-300 cursor-pointer"
                  onClick={() => setLightboxImage(img)}
                >
                  <div className="rounded-lg overflow-hidden border border-white/5 bg-slate-950 aspect-video relative flex items-center justify-center">
                    <AttachmentImage
                      attachmentId={img.id}
                      alt={img.filename}
                      className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <span className="px-3 py-1.5 rounded-lg bg-gradient-to-b from-brand-400 to-brand-600 text-white font-medium text-xs shadow-glow transition-all transform active:scale-95">
                        View Full Image
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 px-1 text-left flex items-center justify-between gap-2">
                    <span
                      className="text-xs text-white truncate font-medium"
                      title={img.filename}
                    >
                      {img.filename}
                    </span>
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">
                      {(img.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassCard>

      <GlassCard>
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-brand-300" /> Assignment History
        </h3>
        <AssignmentTimeline items={activityLogs} assignees={assignees} />
      </GlassCard>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950/90 backdrop-blur-md p-4 transition-all duration-300"
          onClick={() => setLightboxImage(null)}
        >
          <div
            className="relative max-w-5xl w-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-12 right-0 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all shadow-lg"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-full rounded-2xl overflow-hidden border border-white/10 bg-slate-950 flex items-center justify-center p-2 shadow-2xl">
              <AttachmentImage
                attachmentId={lightboxImage.id}
                alt={lightboxImage.filename}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
              />
            </div>
            <div className="text-center text-white">
              <p className="font-semibold text-sm">{lightboxImage.filename}</p>
              <p className="text-xs text-slate-400">
                {(lightboxImage.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------- CommentsTab --------------------------------------------------
function CommentsTab({
  ticketId,
  comments,
  loading,
  isStaff,
  currentUserId,
}: {
  ticketId: string;
  comments: CommentType[];
  loading: boolean;
  isStaff: boolean;
  currentUserId: string;
}) {
  const qc = useQueryClient();
  const [content, setContent] = useState("");
  const [isInternal, setIsInternal] = useState(false);

  const add = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/api/tickets/${ticketId}/comments`, {
          content,
          isInternal,
        })
      ).data.comment,
    onSuccess: () => {
      setContent("");
      setIsInternal(false);
      qc.invalidateQueries({ queryKey: ["ticket.comments", ticketId] });
      qc.invalidateQueries({ queryKey: ["ticket.activity", ticketId] });
      toast.success("Comment posted");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const del = useMutation({
    mutationFn: async (cid: string) =>
      (await api.delete(`/api/comments/${cid}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket.comments", ticketId] });
      toast.success("Comment removed");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <GlassCard>
      <h3 className="text-sm font-semibold text-white mb-3">Conversation</h3>
      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton h-16" />
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-400 py-4">No comments yet.</p>
      ) : (
        <ul className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
          {comments.map((c) => (
            <li
              key={c.id}
              className={cn(
                "rounded-2xl p-3 border",
                c.isInternal
                  ? "bg-amber-500/10 border-amber-400/30"
                  : "bg-white/[0.04] border-white/10",
              )}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2">
                  <Avatar name={c.author?.name || "?"} url={c.author?.avatarUrl} size="xs" />
                  <span className="text-sm text-white">{c.author?.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">
                    {c.author?.role}
                  </span>
                  {c.isInternal && (
                    <span className="badge text-amber-200 bg-amber-500/20 border-amber-400/40">
                      Internal
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400">
                    {fmtRelative(c.createdAt)}
                  </span>
                  {(c.authorId === currentUserId || isStaff) && (
                    <button
                      onClick={() => del.mutate(c.id)}
                      className="btn-icon w-7 h-7"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-200 whitespace-pre-wrap">
                {c.content}
              </p>
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (content.trim()) add.mutate();
        }}
        className="mt-4 pt-4 border-t border-white/5"
      >
        <textarea
          className="textarea"
          rows={3}
          placeholder={
            isInternal ? "Internal note (IT staff only)…" : "Write a comment…"
          }
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
        <div className="mt-2 flex items-center justify-between">
          {isStaff ? (
            <label className="inline-flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
              />
              Internal note (not visible to user)
            </label>
          ) : (
            <span />
          )}
          <button
            type="submit"
            disabled={!content.trim() || add.isPending}
            className="btn-primary"
          >
            {add.isPending ? <Spinner /> : <Send className="w-4 h-4" />}
            Post
          </button>
        </div>
      </form>
    </GlassCard>
  );
}

// ---------- AttachmentsTab -----------------------------------------------
function AttachmentsTab({
  ticketId,
  attachments,
  canDelete,
}: {
  ticketId: string;
  attachments: Attachment[];
  canDelete: (att: Attachment) => boolean;
}) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const [isCustomIssue, setIsCustomIssue] = useState(false);
  const [customIssueText, setCustomIssueText] = useState("");

  const ticketQ = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: async () => (await api.get(`/api/tickets/${ticketId}`)).data,
  });

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/api/tickets/${ticketId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      qc.invalidateQueries({ queryKey: ["ticket", ticketId] });
      qc.invalidateQueries({ queryKey: ["ticket.activity", ticketId] });
      toast.success("File uploaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const del = useMutation({
    mutationFn: async (attId: string) =>
      (await api.delete(`/api/attachments/${attId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ticket", ticketId] });
      toast.success("File removed");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const download = async (att: Attachment) => {
    try {
      const res = await api.get(`/api/attachments/${att.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <GlassCard>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">Files</h3>
        <label
          className={cn(
            "btn-primary cursor-pointer",
            uploading && "opacity-60 pointer-events-none",
          )}
        >
          {uploading ? <Spinner /> : <Paperclip className="w-4 h-4" />}
          Upload
          <input
            type="file"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={uploading}
          />
        </label>
      </div>

      {attachments.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">
          No attachments yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10"
            >
              <div className="w-9 h-9 rounded-xl bg-brand-500/15 border border-brand-400/30 flex items-center justify-center">
                <Paperclip className="w-4 h-4 text-brand-200" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{a.filename}</div>
                <div className="text-[11px] text-slate-400">
                  {(a.size / 1024).toFixed(1)} KB · {fmtRelative(a.createdAt)}
                </div>
              </div>
              <button
                className="btn-icon"
                onClick={() => download(a)}
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
              {canDelete(a) && (
                <button
                  className="btn-icon"
                  onClick={() => del.mutate(a.id)}
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </GlassCard>
  );
}

// ---------- ActivityTab --------------------------------------------------
function ActivityTab({
  items,
  loading,
}: {
  items: ActivityLog[];
  loading: boolean;
}) {
  return (
    <GlassCard>
      <h3 className="text-sm font-semibold text-white mb-3">Activity</h3>
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-10" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">No activity yet.</p>
      ) : (
        <ol className="relative border-l border-white/10 ml-3 space-y-3">
          {items.map((a) => (
            <li key={a.id} className="pl-4 relative">
              <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-brand-400 ring-2 ring-[#04061a]" />
              <div className="text-sm text-white">{a.action}</div>
              <div className="text-[11px] text-slate-400">
                {a.user?.name || "System"} · {fmtDateTime(a.createdAt)}
              </div>
            </li>
          ))}
        </ol>
      )}
    </GlassCard>
  );
}
